/**
 * TaskTracker Component (formerly ConversionTracker)
 * Shows active data processing tasks with progress tracking.
 * Supports both NIfTI to DICOM conversion and Orthanc upload tasks.
 * Displays a panel below with running tools and action buttons.
 */
import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, ChevronDown, ChevronUp, X, CheckCircle, AlertCircle, StopCircle, Upload, Layers } from 'lucide-react';
import API from '../../services/api.ts';
import './ConversionTracker.css';

const POLL_INTERVAL = 1000; // Poll every second

// Task type configurations
const TASK_CONFIGS = {
    convert: {
        progressEndpoint: '/viewers/get_batch_conversion_progress/',
        progressField: 'converted',
        title: 'NIfTI to DICOM Conversion',
        actionLabel: 'Converting',
        completedLabel: 'Converted',
        stoppedLabel: 'Stopped',
        successMessage: (count) => `All ${count} files converted successfully!`,
        stoppedMessage: (done, total) => `Conversion stopped. ${done} of ${total} files converted.`,
        errorMessage: (count) => `${count} file(s) failed to convert`,
        icon: RefreshCw
    },
    upload: {
        progressEndpoint: '/viewers/get_batch_upload_progress/',
        progressField: 'uploaded',
        title: 'Upload to Orthanc',
        actionLabel: 'Uploading',
        completedLabel: 'Uploaded',
        stoppedLabel: 'Stopped',
        successMessage: (count) => `All ${count} files uploaded successfully!`,
        stoppedMessage: (done, total) => `Upload stopped. ${done} of ${total} files uploaded.`,
        errorMessage: (count) => `${count} file(s) failed to upload`,
        icon: Upload
    },
    preupload_annotations: {
        progressEndpoint: '/viewers/get_batch_preupload_annotations_progress/',
        progressField: 'uploaded',
        title: 'Pre-upload Annotations',
        actionLabel: 'Uploading Annotations',
        completedLabel: 'Uploaded',
        stoppedLabel: 'Stopped',
        successMessage: (count) => `All ${count} annotations uploaded successfully!`,
        stoppedMessage: (done, total) => `Annotation upload stopped. ${done} of ${total} uploaded.`,
        errorMessage: (count) => `${count} annotation(s) failed to upload`,
        icon: Layers
    },
    preupload_maps: {
        progressEndpoint: '/viewers/get_batch_preupload_maps_progress/',
        progressField: 'uploaded',
        title: 'Pre-upload Maps',
        actionLabel: 'Uploading Maps',
        completedLabel: 'Uploaded',
        stoppedLabel: 'Stopped',
        successMessage: (count) => `All ${count} maps uploaded successfully!`,
        stoppedMessage: (done, total) => `Map upload stopped. ${done} of ${total} uploaded.`,
        errorMessage: (count) => `${count} map(s) failed to upload`,
        icon: Layers
    }
};

const TaskTracker = ({ taskId, taskType = 'convert', label, onComplete, onClear, onStop }) => {
    const [progress, setProgress] = useState(null);
    const [isOpen, setIsOpen] = useState(true);
    const [error, setError] = useState(null);
    const [stopping, setStopping] = useState(false);
    const pollingRef = useRef(null);
    const dropdownRef = useRef(null);

    const config = TASK_CONFIGS[taskType] || TASK_CONFIGS.convert;
    const TaskIcon = config.icon;

    // Use custom label if provided, otherwise use config default
    const displayLabel = label || config.actionLabel;

    // Start polling when taskId is provided
    useEffect(() => {
        if (taskId) {
            startPolling();
        }
        return () => stopPolling();
    }, [taskId]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const startPolling = () => {
        if (pollingRef.current) return;

        // Initial fetch
        fetchProgress();

        pollingRef.current = setInterval(() => {
            fetchProgress();
        }, POLL_INTERVAL);
    };

    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const fetchProgress = async () => {
        try {
            const response = await API.get(`${config.progressEndpoint}?task_id=${taskId}`);
            setProgress(response.data);

            if (response.data.status === 'completed' || response.data.status === 'stopped') {
                stopPolling();
                if (response.data.status === 'completed' && onComplete) {
                    onComplete(response.data);
                }
            }
        } catch (err) {
            console.error(`Error fetching ${taskType} progress:`, err);
            stopPolling();

            // If task not found (404), clear it from the tracker
            if (err.response?.status === 404) {
                console.log(`${taskType} task not found, clearing tracker`);
                if (onClear) {
                    onClear();
                }
            } else {
                setError('Failed to fetch progress');
            }
        }
    };

    const handleClear = () => {
        stopPolling();
        if (onClear) {
            onClear();
        }
    };

    const handleStop = async () => {
        setStopping(true);
        stopPolling();
        if (onStop) {
            await onStop();
        }
    };

    if (!taskId || !progress) {
        return null;
    }

    // Use the appropriate progress field based on task type
    const progressCount = progress[config.progressField] || 0;
    const progressPercent = progress.total > 0
        ? Math.round((progressCount / progress.total) * 100)
        : 0;

    const isComplete = progress.status === 'completed';
    const isStopped = progress.status === 'stopped';
    const isRunning = !isComplete && !isStopped && !stopping;
    const hasErrors = progress.errors && progress.errors.length > 0;

    return (
        <div className="conversion-tracker" ref={dropdownRef}>
            {/* Toggle Button */}
            <button
                className={`conversion-tracker-btn ${isOpen ? 'active' : ''} ${isComplete ? 'completed' : isStopped ? 'stopped' : 'running'} ${taskType}`}
                onClick={() => setIsOpen(!isOpen)}
                title={config.title}
            >
                {isComplete ? (
                    <CheckCircle size={16} />
                ) : isStopped ? (
                    <StopCircle size={16} />
                ) : (
                    <TaskIcon size={16} className="spinning" />
                )}
                <span className="conversion-label">
                    {isComplete ? config.completedLabel : isStopped ? config.stoppedLabel : displayLabel}
                </span>
                <span className="conversion-count">
                    {progressCount}/{progress.total}
                </span>
                {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {/* Dropdown Panel */}
            {isOpen && (
                <div className="conversion-dropdown">
                    <div className="conversion-dropdown-header">
                        <span>
                            <TaskIcon size={14} />
                            Running Tools
                        </span>
                    </div>

                    <div className="conversion-content">
                        {/* Tool Item */}
                        <div className="conversion-tool-item">
                            <div className={`conversion-tool-icon ${isComplete ? 'completed' : isStopped ? 'stopped' : ''} ${taskType}`}>
                                {isComplete ? (
                                    <CheckCircle size={20} />
                                ) : isStopped ? (
                                    <StopCircle size={20} />
                                ) : (
                                    <TaskIcon size={20} className="spinning" />
                                )}
                            </div>

                            <div className="conversion-tool-info">
                                <div className="conversion-tool-name">{config.title}</div>
                                <div className="conversion-tool-status">
                                    {isComplete ? (
                                        'Completed'
                                    ) : isStopped ? (
                                        'Stopped'
                                    ) : (
                                        <>
                                            {config.actionLabel}:
                                            {progress.current_patient && (
                                                <span className="conversion-tool-patient">{progress.current_patient}</span>
                                            )}
                                            <span className="conversion-tool-file">{progress.current_file || '...'}</span>
                                        </>
                                    )}
                                </div>

                                {/* Progress Bar */}
                                <div className="conversion-progress-section">
                                    <div className="conversion-progress-header">
                                        <span>{progressPercent}%</span>
                                        <span className="conversion-stats">
                                            {progressCount} / {progress.total} files
                                        </span>
                                    </div>
                                    <div className="conversion-progress-bar-container">
                                        <div
                                            className={`conversion-progress-bar ${isComplete ? 'completed' : isStopped ? 'stopped' : ''} ${taskType}`}
                                            style={{ width: `${progressPercent}%` }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="conversion-tool-actions">
                                {isRunning && (
                                    <button
                                        className="conversion-action-btn stop"
                                        onClick={handleStop}
                                        disabled={stopping}
                                        title={`Stop ${taskType}`}
                                    >
                                        <StopCircle size={14} />
                                        Stop
                                    </button>
                                )}
                                {(isComplete || isStopped) && (
                                    <button
                                        className="conversion-action-btn dismiss"
                                        onClick={handleClear}
                                        title="Dismiss"
                                    >
                                        <X size={14} />
                                        Dismiss
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Errors Section */}
                        {hasErrors && (
                            <div className="conversion-errors">
                                <div className="conversion-errors-header">
                                    <AlertCircle size={14} />
                                    {config.errorMessage(progress.errors.length)}
                                </div>
                                <div className="conversion-errors-list">
                                    {progress.errors.slice(0, 3).map((err, idx) => (
                                        <div key={idx} className="conversion-error-item">
                                            <span className="error-filename">{err.filename}</span>
                                            <span className="error-message">{err.error}</span>
                                        </div>
                                    ))}
                                    {progress.errors.length > 3 && (
                                        <div className="conversion-error-more">
                                            +{progress.errors.length - 3} more errors
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Success Message */}
                        {isComplete && !hasErrors && (
                            <div className="conversion-success">
                                <CheckCircle size={16} />
                                <span>{config.successMessage(progressCount)}</span>
                            </div>
                        )}

                        {/* Stopped Message */}
                        {isStopped && (
                            <div className="conversion-stopped">
                                <StopCircle size={16} />
                                <span>{config.stoppedMessage(progressCount, progress.total)}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Keep backward compatibility - export both names
export default TaskTracker;
export { TaskTracker };
